"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { StepProps, CardListingDraft, CardSpecialAttributes, MtgPrintVariant } from "@/types/listing"
import { EMPTY_SPECIAL_ATTRIBUTES } from "@/types/listing"
import { motion } from "framer-motion"
import { useMobileInputScroll } from "@/hooks/use-mobile-input-scroll"
import { DEFAULT_CURRENCY, getCurrencySymbol } from "@/lib/currencies"
import {
  creationFlowInputClass,
  creationFlowBlockClass,
  creationFlowMutedClass,
  creationFlowSectionLabelClass,
  creationFlowTextareaClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { createClient } from "@/utils/supabase/client"
import { DetailsStep } from "@/components/publications/creation-flow/listing-creation/steps/details-step"

const SPECIAL_ATTR_LABELS: { key: keyof CardSpecialAttributes; label: string }[] = [
  { key: "isFirstEdition", label: "1st Edition" },
  { key: "isShadowless", label: "Shadowless" },
  { key: "isReverseHolo", label: "Reverse Holo" },
  { key: "isStampedPromo", label: "Stamped / Promo" },
  { key: "isMisprint", label: "Misprint" },
  { key: "isAltArt", label: "Alt Art" },
  { key: "isSigned", label: "Signed" },
  { key: "isAutograph", label: "Autograph" },
  { key: "isPatchRelic", label: "Patch / Relic" },
  { key: "isRookie", label: "Rookie" },
  { key: "isFoilVariant", label: "Foil variant" },
  { key: "isForeignBlackBorder", label: "Foreign Black Border" },
]

const MTG_VARIANTS: { value: MtgPrintVariant; label: string }[] = [
  { value: "alpha", label: "Alpha" },
  { value: "beta", label: "Beta" },
  { value: "unlimited", label: "Unlimited" },
  { value: "revised", label: "Revised" },
  { value: "fbb", label: "FBB" },
  { value: "fwb", label: "FWB" },
  { value: "summer", label: "Summer" },
  { value: "ce", label: "CE" },
  { value: "ice_age", label: "Ice Age" },
]

const PROVENANCE_OPTIONS = [
  { value: "sealed_pack", label: "Sealed pack pull" },
  { value: "closed_collection", label: "Closed collection" },
  { value: "tournament", label: "Tournament" },
  { value: "resubmitted", label: "Resubmitted" },
  { value: "first_owner", label: "First owner" },
  { value: "original_pack", label: "Original pack" },
]

const MARKET_SOURCES = ["TCGPlayer", "Cardmarket", "eBay sold", "PSA Pop Report", "Other"]

function isValidUrl(s: string): boolean {
  try {
    new URL(s)
    return true
  } catch {
    return false
  }
}

export function CardDetailsStep({
  data,
  updateDataAction,
  onValidationChange,
  onRegisterSave,
  hideButton,
  compact,
  showListingTypeSection,
  showSaleTypeSection,
  allowAuctionOption,
  hideQuantity,
}: StepProps & {
  showListingTypeSection?: boolean
  showSaleTypeSection?: boolean
  allowAuctionOption?: boolean
  hideQuantity?: boolean
}) {
  const t = useTranslations("cardCreation.details")
  const draft = data.cardDraft
  const format = draft?.format
  const [isMtgTcg, setIsMtgTcg] = useState(false)

  const [cardName, setCardName] = useState(draft?.cardName ?? "")
  const [cardNumber, setCardNumber] = useState(draft?.cardNumber ?? "")
  const [printYear, setPrintYear] = useState(draft?.printYear?.toString() ?? "")
  const [commerceValid, setCommerceValid] = useState(true)
  const [specialAttributes, setSpecialAttributes] = useState<CardSpecialAttributes>(
    draft?.specialAttributes ?? { ...EMPTY_SPECIAL_ATTRIBUTES }
  )
  const [mtgPrintVariant, setMtgPrintVariant] = useState<MtgPrintVariant | undefined>(draft?.mtgPrintVariant)
  const [sport, setSport] = useState(draft?.sport ?? "")
  const [playerName, setPlayerName] = useState(draft?.playerName ?? "")
  const [rookieSeason, setRookieSeason] = useState(draft?.rookieSeason ?? "")
  const [printRunNumber, setPrintRunNumber] = useState(draft?.printRunNumber?.toString() ?? "")
  const [printRunTotal, setPrintRunTotal] = useState(draft?.printRunTotal?.toString() ?? "")
  const [lotCardCount, setLotCardCount] = useState(draft?.lotCardCount?.toString() ?? "")
  const [lotCommonsCount, setLotCommonsCount] = useState(draft?.lotCommonsCount?.toString() ?? "")
  const [lotUncommonsCount, setLotUncommonsCount] = useState(draft?.lotUncommonsCount?.toString() ?? "")
  const [lotRaresPlusCount, setLotRaresPlusCount] = useState(draft?.lotRaresPlusCount?.toString() ?? "")
  const [provenanceTags, setProvenanceTags] = useState<string[]>(draft?.provenanceTags ?? [])
  const [provenanceNotes, setProvenanceNotes] = useState(draft?.provenanceNotes ?? "")
  const [certVerifyUrl, setCertVerifyUrl] = useState(draft?.certVerifyUrl ?? "")
  const [previousSaleUrl, setPreviousSaleUrl] = useState(draft?.previousSaleUrl ?? "")
  const [marketReferencePrice, setMarketReferencePrice] = useState(draft?.marketReferencePrice?.toString() ?? "")
  const [marketReferenceSource, setMarketReferenceSource] = useState(draft?.marketReferenceSource ?? "")

  const currencyCode = data.currency || DEFAULT_CURRENCY
  const currencySymbol = getCurrencySymbol(currencyCode)
  const nameInputRef = useMobileInputScroll<HTMLInputElement>()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isSingleOrSlab = format === "single" || format === "slab"
  const isBundleOrCollection = format === "bundle" || format === "collection"
  const showPrintRun = isSingleOrSlab || specialAttributes.isAutograph || specialAttributes.isPatchRelic || specialAttributes.isRookie

  useEffect(() => {
    const tcgId = draft?.tcgId
    if (!tcgId) {
      setIsMtgTcg(false)
      return
    }

    let cancelled = false
    const supabase = createClient()

    Promise.resolve(
      supabase
        .from("taxonomy_items")
        .select("slug, name")
        .eq("id", tcgId)
        .maybeSingle()
    )
      .then(({ data }) => {
        if (cancelled) return
        const candidate = `${data?.slug ?? ""} ${data?.name ?? ""}`.toLowerCase()
        setIsMtgTcg(candidate.includes("magic") || candidate.includes("mtg"))
      })
      .catch(() => {
        if (!cancelled) setIsMtgTcg(false)
      })

    return () => {
      cancelled = true
    }
  }, [draft?.tcgId])

  useEffect(() => {
    if (!isMtgTcg && mtgPrintVariant !== undefined) {
      setMtgPrintVariant(undefined)
    }
  }, [isMtgTcg, mtgPrintVariant])

  const listingFormat = data.listing_format ?? "fixed_price"
  const isOffersOnly = listingFormat === "offers_only"
  const isAuction = listingFormat === "auction"
  const requiresPrice = !isOffersOnly && !isAuction
  const effectivePrice = typeof data.price === "number" ? data.price : 0
  const effectiveQuantity = data.quantity ?? 1
  const isValid = Boolean(
    cardName.trim() &&
    cardName.trim().length <= 200 &&
    commerceValid &&
    (!requiresPrice || (effectivePrice > 0 && effectivePrice <= 99999.99)) &&
    (isOffersOnly || isAuction || (effectiveQuantity >= 1 && Number.isInteger(effectiveQuantity))) &&
    (!certVerifyUrl || isValidUrl(certVerifyUrl)) &&
    (!previousSaleUrl || isValidUrl(previousSaleUrl)) &&
    (!(printRunNumber && printRunTotal) || parseInt(printRunNumber) <= parseInt(printRunTotal))
  )

  useEffect(() => {
    onValidationChange?.(isValid)
  }, [isValid, onValidationChange])

  const saveData = useCallback(() => {
    const cardDraft: CardListingDraft = {
      ...(draft ?? {
        cardCategory: "trading_card" as const,
        format: "single" as const,
        specialAttributes: { ...EMPTY_SPECIAL_ATTRIBUTES },
        isGraded: false,
      }),
      cardName: cardName.trim(),
      cardNumber: cardNumber.trim() || undefined,
      printYear: printYear ? parseInt(printYear) : undefined,
      specialAttributes,
      mtgPrintVariant: isMtgTcg ? mtgPrintVariant || undefined : undefined,
      sport: sport.trim() || undefined,
      playerName: playerName.trim() || undefined,
      rookieSeason: rookieSeason.trim() || undefined,
      printRunNumber: printRunNumber ? parseInt(printRunNumber) : undefined,
      printRunTotal: printRunTotal ? parseInt(printRunTotal) : undefined,
      lotCardCount: lotCardCount ? parseInt(lotCardCount) : undefined,
      lotCommonsCount: lotCommonsCount ? parseInt(lotCommonsCount) : undefined,
      lotUncommonsCount: lotUncommonsCount ? parseInt(lotUncommonsCount) : undefined,
      lotRaresPlusCount: lotRaresPlusCount ? parseInt(lotRaresPlusCount) : undefined,
      provenanceTags: provenanceTags.length ? provenanceTags : undefined,
      provenanceNotes: provenanceNotes.trim() || undefined,
      certVerifyUrl: certVerifyUrl.trim() || undefined,
      previousSaleUrl: previousSaleUrl.trim() || undefined,
      marketReferencePrice: marketReferencePrice ? parseFloat(marketReferencePrice) : undefined,
      marketReferenceSource: marketReferenceSource || undefined,
    }
    updateDataAction({
      cardDraft,
      title: cardName.trim(),
    })
  }, [
    draft, cardName, cardNumber, printYear, specialAttributes,
    mtgPrintVariant, isMtgTcg, sport, playerName, rookieSeason, printRunNumber, printRunTotal,
    lotCardCount, lotCommonsCount, lotUncommonsCount, lotRaresPlusCount,
    provenanceTags, provenanceNotes, certVerifyUrl, previousSaleUrl,
    marketReferencePrice, marketReferenceSource, updateDataAction,
  ])

  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => { onRegisterSave?.(null) }
  }, [saveData, onRegisterSave])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(saveData, 600)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [cardName, saveData])

  const handleCommerceValidation = useCallback((valid: boolean) => {
    setCommerceValid(valid)
  }, [])

  const toggleAttr = (key: keyof CardSpecialAttributes) => {
    setSpecialAttributes((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleProvenance = (tag: string) => {
    setProvenanceTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const sectionLabel = creationFlowSectionLabelClass
  const blockBase = creationFlowBlockClass
  const blockPadding = compact ? "p-3" : "p-4"
  const inputBase = creationFlowInputClass
  const mutedText = creationFlowMutedClass

  const badge = (active: boolean) =>
    cn(
      "rounded-lg px-3 py-1.5 text-xs font-medium cursor-pointer select-none transition-colors",
      active
        ? "bg-foreground/15 text-foreground ring-1 ring-foreground/30"
        : "bg-muted/60 text-muted-foreground hover:bg-muted"
    )

  return (
    <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
      <motion.div className={compact ? "space-y-4" : "space-y-5"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>

        <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("cardInfo")}</p>
          <div className="space-y-3">
            <div>
              <Input
                ref={nameInputRef}
                placeholder={t("cardNamePlaceholder")}
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                maxLength={200}
                className={inputBase}
              />
              <div className={cn("text-xs text-right mt-1", mutedText)}>{cardName.length}/200</div>
            </div>
            <Input
              placeholder={t("cardNumberPlaceholder")}
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              className={inputBase}
            />
            <Input
              placeholder={t("printYearPlaceholder")}
              type="text"
              inputMode="numeric"
              value={printYear}
              onChange={(e) => {
                if (e.target.value === "" || /^\d{0,4}$/.test(e.target.value)) setPrintYear(e.target.value)
              }}
              className={inputBase}
            />
          </div>
        </div>

        <DetailsStep
          data={data}
          updateDataAction={updateDataAction}
          onNextAction={() => {}}
          onPrevAction={() => {}}
          hideButton
          commerceOnly
          compact={compact}
          hideQuantity={hideQuantity}
          showListingTypeSection={showListingTypeSection}
          showSaleTypeSection={showSaleTypeSection}
          allowAuctionOption={allowAuctionOption}
          onValidationChange={handleCommerceValidation}
        />

        {isSingleOrSlab && (
          <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{t("specialAttributes")}</p>
            <div className="flex flex-wrap gap-2">
              {SPECIAL_ATTR_LABELS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleAttr(key)}
                  className={badge(specialAttributes[key])}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {showPrintRun && (
          <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{t("printRun")}</p>
            <div className="flex items-center gap-2">
              <Input
                placeholder="#"
                type="text" inputMode="numeric"
                value={printRunNumber}
                onChange={(e) => { if (e.target.value === "" || /^\d+$/.test(e.target.value)) setPrintRunNumber(e.target.value) }}
                className={cn("w-24", inputBase)}
              />
              <span className="text-sm text-muted-foreground">/</span>
              <Input
                placeholder={t("printRunTotal")}
                type="text" inputMode="numeric"
                value={printRunTotal}
                onChange={(e) => { if (e.target.value === "" || /^\d+$/.test(e.target.value)) setPrintRunTotal(e.target.value) }}
                className={cn("w-24", inputBase)}
              />
            </div>
            {printRunNumber && printRunTotal && parseInt(printRunNumber) > parseInt(printRunTotal) && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t("printRunExceed")}</p>
            )}
          </div>
        )}

        {isMtgTcg || mtgPrintVariant !== undefined ? (
          <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{t("mtgPrintVariant")}</p>
            <div className="flex flex-wrap gap-2">
              {MTG_VARIANTS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setMtgPrintVariant(mtgPrintVariant === v.value ? undefined : v.value)}
                  className={badge(mtgPrintVariant === v.value)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {isBundleOrCollection && (
          <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{t("lotBreakdown")}</p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder={t("totalCards")}
                type="text" inputMode="numeric"
                value={lotCardCount}
                onChange={(e) => { if (e.target.value === "" || /^\d+$/.test(e.target.value)) setLotCardCount(e.target.value) }}
                className={inputBase}
              />
              <Input
                placeholder={t("commons")}
                type="text" inputMode="numeric"
                value={lotCommonsCount}
                onChange={(e) => { if (e.target.value === "" || /^\d+$/.test(e.target.value)) setLotCommonsCount(e.target.value) }}
                className={inputBase}
              />
              <Input
                placeholder={t("uncommons")}
                type="text" inputMode="numeric"
                value={lotUncommonsCount}
                onChange={(e) => { if (e.target.value === "" || /^\d+$/.test(e.target.value)) setLotUncommonsCount(e.target.value) }}
                className={inputBase}
              />
              <Input
                placeholder={t("raresPlus")}
                type="text" inputMode="numeric"
                value={lotRaresPlusCount}
                onChange={(e) => { if (e.target.value === "" || /^\d+$/.test(e.target.value)) setLotRaresPlusCount(e.target.value) }}
                className={inputBase}
              />
            </div>
          </div>
        )}

        {isSingleOrSlab && (
          <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{t("provenance")} <span className={cn("text-xs font-normal normal-case", mutedText)}>{t("optional")}</span></p>
            <div className="flex flex-wrap gap-2 mb-3">
              {PROVENANCE_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => toggleProvenance(p.value)}
                  className={badge(provenanceTags.includes(p.value))}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Textarea
              placeholder={t("provenanceNotes")}
              value={provenanceNotes}
              onChange={(e) => setProvenanceNotes(e.target.value)}
              maxLength={500}
              rows={2}
              className={cn("min-h-[60px] resize-none", creationFlowTextareaClass)}
            />
            <div className="grid grid-cols-1 gap-2 mt-2">
              <Input
                placeholder={t("certVerifyUrl")}
                value={certVerifyUrl}
                onChange={(e) => setCertVerifyUrl(e.target.value)}
                className={inputBase}
              />
              <Input
                placeholder={t("previousSaleUrl")}
                value={previousSaleUrl}
                onChange={(e) => setPreviousSaleUrl(e.target.value)}
                className={inputBase}
              />
            </div>
          </div>
        )}

        <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("marketReference")} <span className={cn("text-xs font-normal normal-case", mutedText)}>{t("optional")}</span></p>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg text-foreground">{currencySymbol}</span>
            <Input
              type="text" inputMode="decimal"
              placeholder={t("referencePrice")}
              value={marketReferencePrice}
              onChange={(e) => {
                const v = e.target.value
                if (v === "" || /^[0-9]*\.?[0-9]{0,2}$/.test(v)) setMarketReferencePrice(v)
              }}
              className={cn("flex-1", inputBase)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {MARKET_SOURCES.map((src) => (
              <button
                key={src}
                type="button"
                onClick={() => setMarketReferenceSource(marketReferenceSource === src ? "" : src)}
                className={badge(marketReferenceSource === src)}
              >
                {src}
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  )
}
